import React, { useEffect, useMemo, useRef, useState } from "react";
import { Search, X, Volume2, VolumeX, Music2, ChevronDown, ChevronUp, Play, Pause, SkipForward, SkipBack } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Boss-Admin floating YouTube mini-player.
 * - Renders only when explicitly mounted (caller checks user email).
 * - Search bar at the top, embedded YouTube IFrame below, transport controls.
 * - Persists position-state (collapsed, muted, volume, last query, queue) in localStorage.
 */

const PRESET = [
  "Punto Muerto Shadow Frontier",
  "La Luna Neto Reino",
  "Pupilas Gera MX",
  "Sweet Dreams Marilyn Manson",
  "Duality Slipknot",
  "Wonderwall Oasis",
];

const LS_KEY = "pk2_yt_player_v1";

function loadState() {
  try {
    const s = JSON.parse(localStorage.getItem(LS_KEY) || "{}");
    return {
      collapsed: !!s.collapsed,
      hidden: !!s.hidden,
      muted: !!s.muted,
      volume: typeof s.volume === "number" ? s.volume : 60,
      query: s.query || "",
      currentIndex: typeof s.currentIndex === "number" ? s.currentIndex : 0,
      queue: Array.isArray(s.queue) ? s.queue : [],
    };
  } catch {
    return { collapsed: false, hidden: false, muted: false, volume: 60, query: "", currentIndex: 0, queue: [] };
  }
}

function saveState(patch) {
  try {
    const cur = JSON.parse(localStorage.getItem(LS_KEY) || "{}");
    localStorage.setItem(LS_KEY, JSON.stringify({ ...cur, ...patch }));
  } catch { /* ignore */ }
}

export default function BossYouTubePlayer() {
  const initial = useMemo(loadState, []);
  const [hidden, setHidden] = useState(initial.hidden);
  const [collapsed, setCollapsed] = useState(initial.collapsed);
  const [query, setQuery] = useState(initial.query);
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [queue, setQueue] = useState(initial.queue);
  const [currentIndex, setCurrentIndex] = useState(initial.currentIndex);
  const [muted, setMuted] = useState(initial.muted);
  const [volume, setVolume] = useState(initial.volume); // 0-100
  const [playing, setPlaying] = useState(false);

  const playerRef = useRef(null); // YT.Player instance
  const iframeRef = useRef(null);
  const ytReady = useRef(false);

  const currentVideo = queue[currentIndex] || null;

  // Persist subset of state
  useEffect(() => { saveState({ hidden, collapsed, muted, volume, query, currentIndex, queue }); }, [hidden, collapsed, muted, volume, query, currentIndex, queue]);

  // Load YT IFrame API
  useEffect(() => {
    if (window.YT && window.YT.Player) { ytReady.current = true; return; }
    if (!document.getElementById("youtube-iframe-api")) {
      const s = document.createElement("script");
      s.id = "youtube-iframe-api";
      s.src = "https://www.youtube.com/iframe_api";
      document.body.appendChild(s);
    }
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      ytReady.current = true;
      if (typeof prev === "function") try { prev(); } catch { /* ignore */ }
    };
  }, []);

  // (Re)create player when iframe div is mounted and we have a video
  useEffect(() => {
    if (hidden || collapsed) return;
    if (!iframeRef.current) return;
    if (!currentVideo) return;

    let cancelled = false;
    const init = () => {
      if (cancelled) return;
      if (!ytReady.current || !window.YT || !window.YT.Player) {
        setTimeout(init, 200); return;
      }
      if (playerRef.current) {
        try { playerRef.current.loadVideoById(currentVideo.videoId); return; } catch { /* recreate */ }
      }
      // eslint-disable-next-line no-new
      playerRef.current = new window.YT.Player(iframeRef.current, {
        videoId: currentVideo.videoId,
        playerVars: { autoplay: 1, rel: 0, modestbranding: 1, playsinline: 1 },
        events: {
          onReady: (e) => {
            try {
              e.target.setVolume(volume);
              if (muted) e.target.mute(); else e.target.unMute();
              e.target.playVideo();
            } catch { /* ignore */ }
          },
          onStateChange: (e) => {
            // 0 = ended, 1 = playing, 2 = paused
            if (e.data === window.YT.PlayerState.ENDED) playNext();
            if (e.data === window.YT.PlayerState.PLAYING) setPlaying(true);
            if (e.data === window.YT.PlayerState.PAUSED) setPlaying(false);
          },
        },
      });
    };
    init();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hidden, collapsed, currentVideo?.videoId]);

  // Keep volume / mute synced with player
  useEffect(() => {
    const p = playerRef.current; if (!p) return;
    try {
      p.setVolume(volume);
      if (muted) p.mute(); else p.unMute();
    } catch { /* ignore */ }
  }, [volume, muted]);

  const runSearch = async (rawQ) => {
    const q = (rawQ ?? query).trim();
    if (!q) return;
    setSearching(true); setShowResults(true);
    try {
      const { data } = await api.get(`/youtube/search?q=${encodeURIComponent(q)}&limit=10`);
      setResults(Array.isArray(data) ? data : []);
    } catch {
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  const playVideo = (vid) => {
    setQueue((q) => {
      const next = [...q];
      // insert just after currentIndex (or replace if empty)
      if (!next.length) { next.push(vid); setCurrentIndex(0); return next; }
      next.splice(currentIndex + 1, 0, vid);
      setCurrentIndex(currentIndex + 1);
      return next;
    });
    setShowResults(false);
  };

  const playNext = () => {
    setCurrentIndex((i) => {
      if (i + 1 < queue.length) return i + 1;
      // auto-load preset song if queue exhausted
      const preset = PRESET[(i + 1) % PRESET.length];
      runSearch(preset).then(() => {});
      return i;
    });
  };
  const playPrev = () => setCurrentIndex((i) => Math.max(0, i - 1));

  const togglePlay = () => {
    const p = playerRef.current; if (!p) return;
    try { (playing ? p.pauseVideo : p.playVideo).call(p); } catch { /* ignore */ }
  };

  const loadPreset = async () => {
    setSearching(true);
    try {
      const all = await Promise.all(
        PRESET.map((q) => api.get(`/youtube/search?q=${encodeURIComponent(q)}&limit=1`).then((r) => r.data?.[0]).catch(() => null))
      );
      const tracks = all.filter(Boolean);
      if (tracks.length) {
        setQueue(tracks);
        setCurrentIndex(0);
      }
    } finally {
      setSearching(false);
    }
  };

  if (hidden) {
    return (
      <button
        onClick={() => setHidden(false)}
        className="fixed z-[400] flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-[0.18em]"
        style={{
          right: 16, bottom: 16,
          background: "rgba(10,10,10,0.92)",
          border: "1px solid var(--brand-primary)",
          color: "var(--brand-primary)",
          boxShadow: "0 0 18px rgba(255,0,51,0.45)",
          backdropFilter: "blur(8px)",
        }}
        data-testid="boss-yt-show"
        aria-label="Mostrar reproductor"
      >
        <Music2 size={14} /> Música
      </button>
    );
  }

  // 10:7 ratio approx. → 360 x ~252 px video; total card a bit taller.
  const cardW = 360;

  return (
    <div
      className="fixed z-[400] flex flex-col"
      data-no-sound
      style={{
        right: 16, bottom: 16, width: cardW,
        background: "rgba(8,8,12,0.94)",
        border: "1px solid var(--brand-primary)",
        boxShadow: "0 0 24px rgba(255,0,51,0.35), 0 8px 30px rgba(0,0,0,0.6)",
        backdropFilter: "blur(10px)",
        fontFamily: "inherit",
      }}
      data-testid="boss-yt-player"
    >
      {/* Header / search */}
      <div
        className="flex items-center gap-2 px-2 py-2"
        style={{ borderBottom: "1px solid rgba(255,0,51,0.35)" }}
      >
        <Music2 size={14} color="var(--brand-primary)" />
        <form
          className="flex-1 flex items-center gap-1"
          onSubmit={(e) => { e.preventDefault(); runSearch(); }}
        >
          <div className="relative flex-1">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar canción en YouTube…"
              className="w-full text-xs px-2 py-1 outline-none"
              style={{
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.12)",
                color: "var(--text-primary)",
              }}
              data-testid="boss-yt-search-input"
            />
          </div>
          <button
            type="submit"
            className="p-1"
            style={{ color: "var(--brand-primary)" }}
            disabled={searching}
            data-testid="boss-yt-search-btn"
            aria-label="Buscar"
          >
            <Search size={14} />
          </button>
        </form>
        <button
          onClick={() => setCollapsed((c) => !c)}
          className="p-1"
          style={{ color: "var(--text-secondary)" }}
          data-testid="boss-yt-collapse"
          aria-label={collapsed ? "Expandir" : "Colapsar"}
          title={collapsed ? "Expandir" : "Colapsar"}
        >
          {collapsed ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>
        <button
          onClick={() => setHidden(true)}
          className="p-1"
          style={{ color: "var(--text-secondary)" }}
          data-testid="boss-yt-hide"
          aria-label="Ocultar"
          title="Ocultar"
        >
          <X size={14} />
        </button>
      </div>

      {!collapsed && (
        <>
          {/* Player area – 10:7 */}
          <div style={{ width: "100%", aspectRatio: "10/7", background: "#000", position: "relative" }}>
            {currentVideo ? (
              <div ref={iframeRef} style={{ width: "100%", height: "100%" }} />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center gap-2 px-4 text-center">
                <p className="text-xs" style={{ color: "var(--text-secondary)" }}>
                  Busca una canción o carga la playlist del Boss.
                </p>
                <button
                  onClick={loadPreset}
                  disabled={searching}
                  className="text-xs px-3 py-1"
                  style={{
                    border: "1px solid var(--brand-primary)",
                    color: "var(--brand-primary)",
                    background: "rgba(255,0,51,0.08)",
                  }}
                  data-testid="boss-yt-load-preset"
                >
                  {searching ? "Cargando…" : "▶ Cargar playlist Boss"}
                </button>
              </div>
            )}
          </div>

          {/* Search results dropdown */}
          {showResults && (
            <div
              className="overflow-y-auto"
              style={{
                maxHeight: 220,
                borderTop: "1px solid rgba(255,255,255,0.08)",
                background: "rgba(0,0,0,0.6)",
              }}
              data-testid="boss-yt-results"
            >
              {searching && (
                <p className="text-xs px-3 py-2" style={{ color: "var(--text-muted)" }}>Buscando…</p>
              )}
              {!searching && results.length === 0 && (
                <p className="text-xs px-3 py-2" style={{ color: "var(--text-muted)" }}>Sin resultados.</p>
              )}
              {!searching && results.map((r) => (
                <button
                  key={r.videoId}
                  onClick={() => playVideo(r)}
                  className="w-full flex items-center gap-2 px-2 py-1 text-left"
                  style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}
                  data-testid={`boss-yt-result-${r.videoId}`}
                >
                  <img src={r.thumbnail} alt="" style={{ width: 56, height: 32, objectFit: "cover" }} />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs truncate" style={{ color: "var(--text-primary)" }}>{r.title}</div>
                    <div className="text-[10px] truncate" style={{ color: "var(--text-muted)" }}>{r.channel}{r.length ? ` · ${r.length}` : ""}</div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Transport + volume */}
          <div className="flex items-center gap-2 px-2 py-2" style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}>
            <button onClick={playPrev} className="p-1" style={{ color: "var(--text-secondary)" }} data-testid="boss-yt-prev" aria-label="Anterior" title="Anterior">
              <SkipBack size={14} />
            </button>
            <button
              onClick={togglePlay}
              className="p-1"
              style={{ color: "var(--brand-primary)" }}
              data-testid="boss-yt-playpause"
              aria-label={playing ? "Pausar" : "Reproducir"}
              title={playing ? "Pausar" : "Reproducir"}
            >
              {playing ? <Pause size={16} /> : <Play size={16} />}
            </button>
            <button onClick={playNext} className="p-1" style={{ color: "var(--text-secondary)" }} data-testid="boss-yt-next" aria-label="Siguiente" title="Siguiente">
              <SkipForward size={14} />
            </button>

            <button
              onClick={() => setMuted((m) => !m)}
              className="p-1"
              style={{ color: muted ? "#ef6d6d" : "var(--text-secondary)" }}
              data-testid="boss-yt-mute"
              aria-label={muted ? "Activar sonido" : "Silenciar"}
              title={muted ? "Activar sonido" : "Silenciar"}
            >
              {muted ? <VolumeX size={14} /> : <Volume2 size={14} />}
            </button>
            <input
              type="range"
              min={0}
              max={100}
              value={volume}
              onChange={(e) => { const v = parseInt(e.target.value, 10); setVolume(v); if (v > 0 && muted) setMuted(false); }}
              className="flex-1 accent-[var(--brand-primary)]"
              style={{ height: 4 }}
              data-testid="boss-yt-volume"
              aria-label="Volumen"
            />
            <span className="text-[10px] tabular-nums w-7 text-right" style={{ color: "var(--text-muted)" }}>{muted ? "🔇" : `${volume}%`}</span>
          </div>

          {/* Now playing */}
          {currentVideo && (
            <div className="px-2 pb-2 flex items-center gap-2 text-[11px]" style={{ color: "var(--text-secondary)" }}>
              <span style={{ color: "var(--brand-primary)" }}>▶</span>
              <span className="truncate" title={currentVideo.title} data-testid="boss-yt-now-playing">{currentVideo.title}</span>
              {queue.length > 1 && (
                <span className="ml-auto text-[10px]" style={{ color: "var(--text-muted)" }}>
                  {currentIndex + 1}/{queue.length}
                </span>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
